import React from 'react';
import { Card } from 'primereact/card';
import { Tag } from 'primereact/tag';
import { Button } from 'primereact/button';
import { ProgressBar } from 'primereact/progressbar';

const StatusCard = ({
  title,
  value,
  icon,
  status,
  trend,
  trendValue,
  color,
  onClick,
  loading,
  subtitle,
  variance,         // Nova propriedade: variância percentual
  isVarianceAlert   // Nova propriedade: alerta de variância
}) => {
  const getSeverity = (status) => {
    switch (status) {
      case 'success':
        return 'success';
      case 'warning':
        return 'warning';
      case 'danger':
        return 'danger';
      case 'info':
        return 'info';
      case 'help':
        return 'help';
      default:
        return 'secondary';
    }
  };

  const getColorStyle = (color, isVarianceAlert) => {
    // Se há alerta de variância, usa um verde mais forte
    const baseColors = {
      green: isVarianceAlert ? '#16a34a' : '#22c55e',
      blue: '#3b82f6',
      orange: '#f97316',
      red: '#ef4444',
      purple: '#a855f7',
      gray: '#6b7280'
    };

    const colorValue = baseColors[color] || baseColors.gray;

    // Cores de fundo mais suaves baseadas na cor principal
    const bgColors = {
      green: 'rgba(34, 197, 94, 0.2)',
      blue: 'rgba(59, 130, 246, 0.2)',
      orange: 'rgba(249, 115, 22, 0.2)',
      red: 'rgba(239, 68, 68, 0.2)',
      purple: 'rgba(168, 85, 247, 0.2)',
      gray: 'rgba(107, 114, 128, 0.2)'
    };

    return {
      accentColor: colorValue,
      borderLeftColor: colorValue,
      borderLeftWidth: isVarianceAlert ? '6px' : '4px',
      backgroundColor: bgColors[color] || bgColors.gray
    };
  };


  const formatVariance = (variance) => {
    if (variance === null || variance === undefined) return null;
    const sign = variance >= 0 ? '+' : '';
    return `${sign}${variance}%`;
  };

  return (
    <Card
      className={`transition-all hover:shadow-lg relative ${onClick ? 'hover:scale-105 cursor-pointer' : ''}`}
      style={ color ? getColorStyle(color, isVarianceAlert) : {}}
      onClick={onClick}
      pt={{ content: { className: 'p-4 sm:p-4' }, body: { className: 'p-0' } }}
    >
      {color && (
        <div
          className="absolute left-0 right-0 bottom-0"
          style={{ height: '6px', backgroundColor: getColorStyle(color, isVarianceAlert).accentColor }}
        />
      )}
      <div className="flex flex-row flex-wrap sm:flex-nowrap items-center justify-between gap-2 sm:gap-4 w-full pb-0">



        {/* Left: Icon and Title (Adicionado min-w-0 e flex-1 para controlar o espaço) */}
        <div className="flex items-center gap-2 flex-1 min-w-0">
          {icon && (
            <span className="text-xl sm:text-2xl text-gray-500 shrink-0">
              <i className={icon}></i>
            </span>
          )}
          <div className="flex flex-col min-w-0">
            {/* truncate ou break-words evita que o título empurre o número */}
            <span className="text-xs sm:text-sm text-gray-600 font-medium truncate">
              {title}
            </span>
            {subtitle && (
              <div className="text-xs text-gray-400 truncate">
                {subtitle}
              </div>
            )}
          </div>
        </div>

        {/* Right: Value and Status */}
        <div className="flex flex-col items-end shrink-0">
          {loading ? (
            <ProgressBar mode="indeterminate" className="h-2 w-20 sm:w-32" />
          ) : (
            <>
              {/* Fonte responsiva: text-xl no mobile, text-3xl no desktop */}
              <div className="text-xl sm:text-xl font-bold text-gray-600">
                {value !== undefined ? value : '-'}
              </div>

              {/* Trend */}
              {trend !== undefined && variance === null && (
                <div className={`text-xs sm:text-sm flex items-center gap-1 ${trend === 'up' ? 'text-green-500' :
                    trend === 'down' ? 'text-red-600' : 'text-gray-500'
                  }`}>
                  {trend === 'up' && <i className="pi pi-arrow-up text-xs"></i>}
                  {trend === 'down' && <i className="pi pi-arrow-down text-xs"></i>}
                  <span>{trendValue}</span>
                </div>
              )}
            </>
          )}

          {status && (
            <Tag
              value={status}
              severity={getSeverity(status)}
              icon={status === 'success' ? 'pi pi-check-circle' :
                status === 'warning' ? 'pi pi-exclamation-triangle' :
                  status === 'danger' ? 'pi pi-times-circle' : 'pi pi-info-circle'}
              className="mt-0 sm:mt-1 text-xs"
            />
          )}
        </div>
      </div>
    </Card>
  );
};

// Status summary component for dashboard
export const StatusSummary = ({ stats = {}, onStatusClick }) => {
  // Fonte única da lógica de “sinalização” do dashboard (label/cor/ícone)
  const statusOrder = [
    'SUCCESS',
    'FAILED',
    'MANUAL_PENDING',
    'PENDING',
    'EXPORTED'
  ];

  const statusConfig = {
    SUCCESS: { title: 'A Exportar', color: 'blue', icon: 'pi pi-check' },
    FAILED: { title: 'Erro', color: 'red', icon: 'pi pi-times' },
    MANUAL_PENDING: { title: 'Informar Dados', color: 'orange', icon: 'pi pi-clock' },
    PENDING: { title: 'Pendentes', color: 'gray', icon: 'pi pi-inbox' },
    EXPORTED: { title: 'Exportados', color: 'green', icon: 'pi pi-upload' }
  };

  return (
    <div className="grid gap-3 mt-2" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', marginLeft:'2px', marginRight:'2px' }}>
      {statusOrder.map((status) => {
        const cfg = statusConfig[status] || { title: status, color: 'gray', icon: 'pi pi-circle' };
        const value = stats?.[status] ?? 0;

        return (
          <StatusCard
            key={status}
            title={cfg.title}
            value={value}
            icon={cfg.icon}
            color={cfg.color}
            onClick={() => onStatusClick?.(status)}
          />
        );
      })}
    </div>
  );
};

export default StatusCard;